import { PathFinder, AlgorithmOptions } from '../core/PathFinder';
import { PathResult, AlgorithmStep } from '../core/PathResult';
import { BaseGrid } from '@/grid/BaseGrid';
import { Cell } from '@/grid/Cell';
import { Heuristic } from '../core/Heuristic';
import { PriorityQueue } from '@/utils/PriorityQueue';
import { ClusterGrid } from './ClusterGrid';
import { AbstractGraph, AbstractNode } from './AbstractGraph';
import { EntrancePoint } from './EntrancePoint';
import { IntraClusterCache } from './IntraClusterCache';
import { ClusterPathRefinement } from './ClusterPathRefinement';
import { AStar } from '../algorithms/AStar';
import { Cluster } from './Cluster';

export interface HPAStarOptions extends AlgorithmOptions {
  clusterSize?: number;
  maxCacheSize?: number;
}

export class HierarchicalAStar extends PathFinder {
  readonly name = 'HPA* (Hierarchical)';
  readonly description = 'Multi-level pathfinding with cluster abstraction';

  private clusterGrid: ClusterGrid | null = null;
  private abstractGraph: AbstractGraph | null = null;
  private intraCache: IntraClusterCache;

  private preprocessingTime: number = 0;
  private abstractSearchTime: number = 0;
  private refinementTime: number = 0;
  private abstractNodesExplored: number = 0;
  private localNodesExplored: number = 0;
  private peakOpenListSize: number = 0;
  private peakClosedListSize: number = 0;
  private cacheHits: number = 0;

  constructor() {
    super();
    this.intraCache = new IntraClusterCache(1000);
  }

  private getDefaultClusterSize(grid: BaseGrid): number {
    const totalCells = grid.rows * grid.cols;
    const targetClusterCells = 100;
    const clusterSize = Math.max(
      3,
      Math.ceil(Math.sqrt(totalCells / targetClusterCells))
    );
    return Math.min(clusterSize, 20);
  }

  findPath(
    grid: BaseGrid,
    start: Cell,
    goal: Cell,
    options?: HPAStarOptions
  ): PathResult {
    const startTime = performance.now();
    const steps: AlgorithmStep[] = [];

    const generator = this.findPathStepByStep(grid, start, goal, options);

    let result = generator.next();
    while (!result.done) {
      steps.push(result.value);
      result = generator.next();
    }

    const executionTime = performance.now() - startTime;
    const pathResult = result.value;

    return new PathResult(
      pathResult.path,
      pathResult.nodesExplored,
      executionTime,
      pathResult.success,
      steps
    );
  }

  *findPathStepByStep(
    grid: BaseGrid,
    start: Cell,
    goal: Cell,
    options?: HPAStarOptions
  ): Generator<AlgorithmStep, PathResult, undefined> {
    const startTime = performance.now();
    grid.reset();

    const clusterSize = options?.clusterSize || this.getDefaultClusterSize(grid);
    const maxCacheSize = options?.maxCacheSize || 1000;

    this.intraCache.clear();
    this.intraCache = new IntraClusterCache(maxCacheSize);

    this.abstractNodesExplored = 0;
    this.localNodesExplored = 0;
    this.cacheHits = 0;
    this.peakOpenListSize = 0;
    this.peakClosedListSize = 0;

    const preprocessStart = performance.now();
    yield* this.preprocessGrid(grid, clusterSize);
    this.preprocessingTime = performance.now() - preprocessStart;

    const clusterGrid = this.clusterGrid!;
    const abstractGraph = this.abstractGraph!;

    const startCluster = clusterGrid.getClusterForCell(start);
    const goalCluster = clusterGrid.getClusterForCell(goal);

    if (!startCluster || !goalCluster) {
      const executionTime = performance.now() - startTime;
      return PathResult.failure(0, executionTime);
    }

    const abstractStart = this.insertStartGoalNodes(
      start,
      startCluster,
      abstractGraph,
      grid
    );
    const abstractGoal = this.insertStartGoalNodes(
      goal,
      goalCluster,
      abstractGraph,
      grid
    );

    const abstractSearchStart = performance.now();
    const abstractPath = yield* this.abstractSearch(
      abstractStart,
      abstractGoal,
      abstractGraph
    );
    this.abstractSearchTime = performance.now() - abstractSearchStart;

    if (abstractPath.length === 0) {
      const executionTime = performance.now() - startTime;
      return PathResult.failure(this.abstractNodesExplored, executionTime);
    }

    const refinementStart = performance.now();
    const refinementResult = yield* ClusterPathRefinement.refineAbstractPath(
      abstractPath,
      grid,
      this.intraCache
    );
    this.refinementTime = performance.now() - refinementStart;

    this.localNodesExplored += refinementResult.localNodesExplored;
    this.cacheHits += refinementResult.cacheHits;

    const finalPath = refinementResult.path;
    const executionTime = performance.now() - startTime;
    const totalNodesExplored = this.abstractNodesExplored + this.localNodesExplored;

    return new PathResult(
      finalPath,
      totalNodesExplored,
      executionTime,
      finalPath.length > 0
    );
  }

  private *preprocessGrid(
    grid: BaseGrid,
    clusterSize: number
  ): Generator<AlgorithmStep, void, undefined> {
    this.clusterGrid = new ClusterGrid(grid, clusterSize);
    const clusters = this.clusterGrid.getAllClusters();

    for (const cluster of clusters) {
      yield {
        type: 'open',
        cell: cluster.cells[0],
        openListSize: 0,
        closedListSize: 0,
        metadata: {
          phase: 'preprocessing',
          action: 'cluster_boundary',
          clusterId: cluster.id
        }
      };
    }

    const entrancePoints = this.identifyEntrancePoints(grid, this.clusterGrid);

    for (const entrance of entrancePoints) {
      yield {
        type: 'open',
        cell: entrance.getCenter(),
        openListSize: 0,
        closedListSize: 0,
        metadata: {
          phase: 'preprocessing',
          action: 'entrance_point',
          entranceId: entrance.id
        }
      };
    }

    this.abstractGraph = this.buildAbstractGraph(
      grid,
      this.clusterGrid,
      entrancePoints
    );

    for (const node of this.abstractGraph.getAllNodes()) {
      yield {
        type: 'close',
        cell: node.position,
        openListSize: 0,
        closedListSize: 0,
        metadata: {
          phase: 'preprocessing',
          action: 'abstract_node',
          nodeId: node.id
        }
      };
    }
  }

  private identifyEntrancePoints(
    grid: BaseGrid,
    clusterGrid: ClusterGrid
  ): EntrancePoint[] {
    const entrances: EntrancePoint[] = [];
    let entranceId = 0;

    const clusters = clusterGrid.getAllClusters();

    for (const cluster of clusters) {
      const adjacent = clusterGrid.getAdjacentClusters(cluster);

      for (const neighbor of adjacent) {
        if (cluster.id >= neighbor.id) continue;

        const boundaryPairs = this.findBoundaryPairs(cluster, neighbor, grid);

        for (const [cell1, cell2] of boundaryPairs) {
          if (cell1.isWalkable && cell2.isWalkable) {
            const entrance = new EntrancePoint(
              entranceId++,
              cell1,
              cell2,
              cluster,
              neighbor
            );
            entrances.push(entrance);
            cluster.addEntrancePoint(entrance);
            neighbor.addEntrancePoint(entrance);
          }
        }
      }
    }

    return entrances;
  }

  private findBoundaryPairs(
    cluster1: Cluster,
    cluster2: Cluster,
    grid: BaseGrid
  ): Array<[Cell, Cell]> {
    const pairs: Array<[Cell, Cell]> = [];

    const b1 = cluster1.bounds;
    const b2 = cluster2.bounds;

    if (
      b1.maxCol + 1 === b2.minCol ||
      b2.maxCol + 1 === b1.minCol
    ) {
      const col = b1.maxCol + 1 === b2.minCol ? b1.maxCol : b2.maxCol;

      const minRow = Math.max(b1.minRow, b2.minRow);
      const maxRow = Math.min(b1.maxRow, b2.maxRow);

      for (let row = minRow; row <= maxRow; row++) {
        const cell1 = grid.getCell(row, col);
        const cell2 =
          col === b1.maxCol
            ? grid.getCell(row, col + 1)
            : grid.getCell(row, col - 1);

        if (cell1 && cell2) {
          pairs.push([cell1, cell2]);
        }
      }
    } else if (
      b1.maxRow + 1 === b2.minRow ||
      b2.maxRow + 1 === b1.minRow
    ) {
      const row =
        b1.maxRow + 1 === b2.minRow ? b1.maxRow : b2.maxRow;

      const minCol = Math.max(b1.minCol, b2.minCol);
      const maxCol = Math.min(b1.maxCol, b2.maxCol);

      for (let col = minCol; col <= maxCol; col++) {
        const cell1 = grid.getCell(row, col);
        const cell2 =
          row === b1.maxRow
            ? grid.getCell(row + 1, col)
            : grid.getCell(row - 1, col);

        if (cell1 && cell2) {
          pairs.push([cell1, cell2]);
        }
      }
    }

    return pairs;
  }

  private buildAbstractGraph(
    grid: BaseGrid,
    clusterGrid: ClusterGrid,
    entrances: EntrancePoint[]
  ): AbstractGraph {
    const graph = new AbstractGraph();
    const nodeMap = new Map<number, AbstractNode>();

    for (const entrance of entrances) {
      if (entrance.isTransitionable) {
        const node = graph.addNode(entrance);
        nodeMap.set(entrance.id, node);
      }
    }

    const aStar = new AStar();

    for (const cluster of clusterGrid.getAllClusters()) {
      const clusterEntrances = cluster.entrancePoints.filter(
        e => e.isTransitionable
      );

      for (let i = 0; i < clusterEntrances.length; i++) {
        for (let j = i + 1; j < clusterEntrances.length; j++) {
          const entrance1 = clusterEntrances[i];
          const entrance2 = clusterEntrances[j];

          const node1 = nodeMap.get(entrance1.id);
          const node2 = nodeMap.get(entrance2.id);

          if (!node1 || !node2) continue;

          const result = aStar.findPath(
            grid,
            entrance1.getCenter(),
            entrance2.getCenter(),
            { heuristic: Heuristic.manhattan }
          );

          if (result.success) {
            const cost = result.path.length;
            graph.addEdge(node1, node2, cost, result.path);
            graph.addEdge(node2, node1, cost, [...result.path].reverse());
          }
        }
      }
    }

    return graph;
  }

  private insertStartGoalNodes(
    cell: Cell,
    cluster: Cluster,
    graph: AbstractGraph,
    grid: BaseGrid
  ): AbstractNode {
    const aStar = new AStar();
    const clusterEntrances = cluster.entrancePoints.filter(
      (e: EntrancePoint) => e.isTransitionable
    );

    const tempNode = new AbstractNode(-1, clusterEntrances[0]);
    const tempCell = {
      row: cell.row,
      col: cell.col,
      gCost: cell.gCost,
      hCost: cell.hCost,
      fCost: cell.fCost,
      parent: cell.parent,
      equals: (other: Cell) => other.row === cell.row && other.col === cell.col,
    } as any;
    (tempNode.position as any) = tempCell;

    for (const entrance of clusterEntrances) {
      const abstractNode = graph
        .getAllNodes()
        .find(n => n.entrance.id === entrance.id);
      if (!abstractNode) continue;

      const result = aStar.findPath(
        grid,
        cell,
        entrance.getCenter(),
        { heuristic: Heuristic.manhattan }
      );

      if (result.success) {
        const cost = result.path.length;
        graph.addEdge(tempNode, abstractNode, cost, result.path);
        graph.addEdge(abstractNode, tempNode, cost, [...result.path].reverse());
      }
    }

    return tempNode;
  }

  private *abstractSearch(
    startNode: AbstractNode,
    goalNode: AbstractNode,
    graph: AbstractGraph
  ): Generator<AlgorithmStep, AbstractNode[], undefined> {
    graph.reset();

    const openSet = new PriorityQueue<AbstractNode>();
    const closedSet = new Set<AbstractNode>();

    startNode.gCost = 0;
    startNode.hCost = Heuristic.manhattan(startNode.position, goalNode.position);
    startNode.fCost = startNode.gCost + startNode.hCost;
    startNode.parent = null;

    openSet.enqueue(startNode, startNode.fCost);

    yield {
      type: 'open',
      cell: startNode.position,
      cost: startNode.fCost,
      openListSize: openSet.size,
      closedListSize: closedSet.size,
      metadata: {
        phase: 'abstract_search',
        action: 'open_abstract',
        nodeId: startNode.id
      }
    };

    while (!openSet.isEmpty()) {
      this.peakOpenListSize = Math.max(this.peakOpenListSize, openSet.size);

      const current = openSet.dequeue();
      if (!current) break;

      if (current.equals(goalNode)) {
        const path: AbstractNode[] = [];
        let node: AbstractNode | null = current;
        while (node !== null) {
          path.unshift(node);
          node = node.parent;
        }

        yield {
          type: 'path_found',
          cell: current.position,
          cost: current.fCost,
          openListSize: openSet.size,
          closedListSize: closedSet.size,
          metadata: { phase: 'abstract_search', action: 'path_found' }
        };

        return path;
      }

      closedSet.add(current);
      this.peakClosedListSize = Math.max(
        this.peakClosedListSize,
        closedSet.size
      );
      this.abstractNodesExplored++;

      yield {
        type: 'close',
        cell: current.position,
        cost: current.fCost,
        openListSize: openSet.size,
        closedListSize: closedSet.size,
        metadata: {
          phase: 'abstract_search',
          action: 'close_abstract',
          nodeId: current.id
        }
      };

      for (const edge of current.edges) {
        const neighbor = edge.to;

        if (closedSet.has(neighbor)) {
          continue;
        }

        const tentativeGCost = current.gCost + edge.cost;

        if (tentativeGCost < neighbor.gCost) {
          neighbor.parent = current;
          neighbor.gCost = tentativeGCost;
          neighbor.hCost = Heuristic.manhattan(
            neighbor.position,
            goalNode.position
          );
          neighbor.fCost = neighbor.gCost + neighbor.hCost;

          const isInOpenSet = openSet.contains(neighbor, (a, b) =>
            a.equals(b)
          );

          if (isInOpenSet) {
            openSet.updatePriority(neighbor, neighbor.fCost, (a, b) =>
              a.equals(b)
            );
          } else {
            openSet.enqueue(neighbor, neighbor.fCost);

            yield {
              type: 'open',
              cell: neighbor.position,
              cost: neighbor.fCost,
              openListSize: openSet.size,
              closedListSize: closedSet.size,
              metadata: {
                phase: 'abstract_search',
                action: 'open_abstract',
                nodeId: neighbor.id
              }
            };
          }
        }
      }
    }

    return [];
  }

  getVisualizationData() {
    return {
      clusters: this.clusterGrid?.getAllClusters() || [],
      entrances: this.getEntrancePoints(),
      abstractGraph: this.abstractGraph || null,
      currentAbstractPath: []
    };
  }

  private getEntrancePoints(): EntrancePoint[] {
    if (!this.clusterGrid) return [];

    const entrances: EntrancePoint[] = [];
    const seen = new Set<number>();

    for (const cluster of this.clusterGrid.getAllClusters()) {
      for (const entrance of cluster.entrancePoints) {
        if (!seen.has(entrance.id)) {
          entrances.push(entrance);
          seen.add(entrance.id);
        }
      }
    }

    return entrances;
  }

  getStatistics() {
    const cacheStats = this.intraCache.getStats();

    return {
      preprocessingTime: this.preprocessingTime,
      abstractSearchTime: this.abstractSearchTime,
      refinementTime: this.refinementTime,
      abstractNodesExplored: this.abstractNodesExplored,
      localNodesExplored: this.localNodesExplored,
      peakOpenListSize: this.peakOpenListSize,
      peakClosedListSize: this.peakClosedListSize,
      cacheSize: cacheStats.size,
      cacheHitRate: cacheStats.hitRate,
      optimalityRatio: null as number | null
    };
  }
}
